import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './InterviewForm.css';
import Dialog from '@mui/material/Dialog';
import PaymentIcon from "@mui/icons-material/Payment";
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { getApiCall, postApiCall } from '../utils/Apicalls';
import { generateToken } from "../utils/tokenGenerate";
import { encryptData } from "../utils/simpleCrypto";
import env from '../utils/env';

function InterviewForm({ onStartSession }) {
  // Form fields
  const [interView, setInterView] = useState('webApp');
  const [interViewTime, setInterViewTime] = useState(0);
  const [jobTitle, setJobTitle] = useState('');
  const [skillset, setSkillset] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [resumeFile, setResumeFile] = useState(null);
  const [userDetails, setUserDetails] = useState({
    id: '',
    name: '',
    email: ''
  });
  const [showDesktopError, setShowDesktopError] = useState('');

  // Validation states
  const [fileError, setFileError] = useState('');
  const [jobDescriptionError, setJobDescriptionError] = useState('');

  // Recording states
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState('');
  const mediaRecorderRef = useRef(null);
  const recordingTimerRef = useRef(null);

  // Session states
  const [showTimeAlert, setShowTimeAlert] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [sessionTimeRemaining, setSessionTimeRemaining] = useState(0);
  const [sessionWebTimeRemaining, setSessionWebTimeRemaining] = useState(0);
  const [sessionDesktopTimeRemaining, setSessionDesktopTimeRemaining] = useState(0);
  const [isStartingSession, setIsStartingSession] = useState(false);
  const navigate = useNavigate();

  // Check for remaining time from API
  useEffect(() => {
    let userData = localStorage.getItem("userDetails");
    if (userData) {
      userData = JSON.parse(userData);
      let name = userData?.first_name || "";
      if (userData?.last_name) {
        name += " " + userData?.last_name;
      }
      setUserDetails({
        id: userData?.id || '',
        name: name,
        email: userData?.email || ""
      });
    }
    const checkRemainingTime = async () => {
      try {
        const result = await getApiCall('interview-app/interview/details');
        if (result && result.result) {
          // Store the remaining time in minutes
          setSessionTimeRemaining(result.result.webAppRemainingMinutes || 0);
          setSessionWebTimeRemaining(result.result.webAppRemainingMinutes || 0);
          setSessionDesktopTimeRemaining(result.result.desktopAppRemainingMinutes || 0);
          
          if (result && result.result && result.result.startInterviewDetails) {
            const sessionId = result.result.startInterviewDetails.id;
            localStorage.setItem('currentInterviewSessionId', sessionId);
            onStartSession({
              type: result.result.startInterviewDetails?.type || interView,
              interViewTime,
              jobTitle,
              skillset,
              jobDescription,
              resumeFile,
              audioBlob,
              sessionTimeRemaining,
              sessionId: sessionId
            });
          }
        }
      } catch (error) {
        console.error('Error fetching remaining time:', error);
      }
    };

    checkRemainingTime();
  }, []);

  // Display remaining time in the form
  const renderRemainingTime = () => {
    if (sessionTimeRemaining > 0) {
      return (
        <div className="remaining-time-display">
          <AccessTimeIcon className="time-icon" />
          <span>You have {sessionTimeRemaining} {sessionTimeRemaining === 1 ? 'minute' : 'minutes'} remaining. Unlimited FREE minutes. Email info@interviewsupport-ai.com for more FREE minutes.</span>
        </div>
      );
    }
    return null;
  };

  // Start recording voice
  const startRecording = async () => {
    try {
      // Reset recording state
      setRecordingTime(0);
      setAudioBlob(null);
      setAudioUrl('');

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Create media recorder
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      // Set up data handling
      const audioChunks = [];
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      };

      // Handle recording stop
      mediaRecorder.onstop = () => {
        // Create blob from chunks
        const audioBlob = new Blob(audioChunks, { type: 'audio/wav' });
        const audioUrl = URL.createObjectURL(audioBlob);

        // Update state
        setAudioBlob(audioBlob);
        setAudioUrl(audioUrl);
        setIsRecording(false);

        // Stop all tracks in the stream
        stream.getTracks().forEach(track => track.stop());
      };

      // Start recording
      mediaRecorder.start();
      setIsRecording(true);

      // Set up timer (limit to 2 minutes = 120 seconds)
      const MAX_RECORDING_TIME = 120;
      let seconds = 0;

      recordingTimerRef.current = setInterval(() => {
        seconds += 1;
        setRecordingTime(seconds);

        // Auto-stop after 2 minutes
        if (seconds >= MAX_RECORDING_TIME) {
          stopRecording();
        }
      }, 1000);
    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Could not access microphone. Please check your permissions.');
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      clearInterval(recordingTimerRef.current);
    }
  };

  // Play recorded audio
  const playRecording = () => {
    if (audioUrl) {
      const audio = new Audio(audioUrl);
      audio.play();
    }
  };

  // Delete recorded audio
  const deleteRecording = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioBlob(null);
      setAudioUrl('');
      setRecordingTime(0);
    }
  };

  // Format recording time as MM:SS
  const formatRecordingTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Validate job description word count
  const validateJobDescription = (text) => {
    if (!text) return true;

    const wordCount = text.trim().split(/\s+/).length;
    if (wordCount > 10000) {
      setJobDescriptionError(`Job description exceeds 10,000 words (current: ${wordCount})`);
      return false;
    }

    setJobDescriptionError('');
    return true;
  };

  // Start interview session with API
  const startInterviewSession = async () => {
    try {
      // Extract primary technology from skillset
      const primaryTechnology = skillset.split(',')[0].trim() || jobTitle;

      let params = {
        technology: primaryTechnology,
        type: interView,
        interViewTime: interView === 'desktopApp' ? parseInt(interViewTime) || 0 : 0,
      };
      // Call the start interview API
      const result = await postApiCall('interview-app/interview/start', params);
      if (result && result.status && result.result && result.result.inserted) {
        if (interView === "desktopApp") {
          const tokenParams = {
            userId: userDetails?.id || "",
            name: userDetails?.name || "",
            email: userDetails?.email || "",
            resume: "",
            skills: [skillset],
            jobDescription: jobDescription,
            sessionStart: new Date().toISOString(),
            sessionDurationMinutes: params.interViewTime || 0,
            OpenApiKey: env.OPENAI_API_KEY,
            AzureApiKey: env.AZURE_API_KEY
          };
          const encryptOpenApiKey = await encryptData(env.OPENAI_API_KEY, env.DESKTOP_API_SECRET);
          if (encryptOpenApiKey) {
            tokenParams.OpenApiKey = encryptOpenApiKey;
          }
          const encryptAzureApiKey = await encryptData(env.AZURE_API_KEY, env.DESKTOP_API_SECRET);
          if (encryptAzureApiKey) {
            tokenParams.AzureApiKey = encryptAzureApiKey;
          }
          const desktopToken = await generateToken(tokenParams, params.interViewTime+"m");
          localStorage.setItem('desktopToken', desktopToken);
          localStorage.setItem('tokenParams', JSON.stringify({
            interViewTime: params.interViewTime || 0,
            skills: [skillset],
            sessionStart: tokenParams.sessionStart,
            interviewType: "desktopApp",
            jobTitle: jobTitle || "",
            jobDescription: jobDescription,
          }));
          navigate('/generate-token');
          return;
        }

        setIsStartingSession(true);
    
        // Store the session ID in localStorage for later use
        localStorage.setItem('interView', interView);
        localStorage.setItem('currentInterviewSessionId', result.result.inserted);
        
        // Create session config
        const config = {
          type: interView,
          interViewTime,
          jobTitle,
          skillset,
          jobDescription,
          resumeFile,
          audioBlob,
          sessionTimeRemaining,
          sessionId: result.result.inserted
        };
        
        // Pass the configuration to the parent component
        onStartSession(config);
      } else {
        console.error('Failed to start interview session:', result);
        alert('Failed to start interview session. Please try again.');
      }
    } catch (error) {
      console.error('Error starting interview session:', error);
      alert('An error occurred while starting the interview session. Please try again.');
    } finally {
      if (interView === "webApp") {
        setIsStartingSession(false);
      }
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    setShowDesktopError('');

    // Validate form
    if (!jobTitle.trim()) {
      return; // Don't submit if job title is empty
    }

    if (sessionTimeRemaining && interView === 'desktopApp' && (!interViewTime || isNaN(interViewTime) || interViewTime <= 0)) {
      setShowDesktopError('Please enter a valid interview time for Desktop App');
      return; // Don't submit if desktop app time is invalid
    }

    if (!validateJobDescription(jobDescription)) {
      return; // Don't submit if job description is too long
    }

    // Check if user has remaining time
    if (sessionTimeRemaining <= 0) {
      // No time remaining, show payment dialog
      setShowPaymentDialog(true);
    } else {
      // Has time remaining, show time alert
      setShowTimeAlert(true);
    }
  };

  // Handle confirmation from time alert dialog
  const handleTimeAlertConfirm = () => {
    setShowTimeAlert(false);
    startInterviewSession();
  };

  // Handle payment dialog actions
  const handlePaymentDialogClose = () => {
    setShowPaymentDialog(false);
  };

  const handlePaymentConfirm = () => {
    setShowPaymentDialog(false);
    // Redirect to checkout page
    let url = "/checkout";
    if (interView === 'desktopApp') {
      url = '/checkout/' + env.DESKTOP_PLAN_ID;
    } else if (interView === 'webApp') {
      url = '/checkout/' + env.WEB_PLAN_ID;
    }
    navigate(url);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFileError('');

    if (!file) {
      setResumeFile(null);
      return;
    }

    // Check file type
    const fileType = file.type;
    const validTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    if (!validTypes.includes(fileType)) {
      setFileError('Please upload a PDF or DOCX file');
      setResumeFile(null);
      e.target.value = ''; // Reset file input
      return;
    }

    // Check file size (limit to 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setFileError('File size should be less than 5MB');
      setResumeFile(null);
      e.target.value = ''; // Reset file input
      return;
    }

    setResumeFile(file);
  };

  const clearResume = () => {
    setResumeFile(null);
    setFileError('');
    // Reset the file input
    const fileInput = document.getElementById('resumeUpload');
    if (fileInput) fileInput.value = '';
  };

  return (
    <div className="interview-form">
      <h2>Simulate Your Interview Session</h2>
      {renderRemainingTime()}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <div className='row'>
            <div className='col-8'>&nbsp;</div>
            <div className="col-4 mb-3">
              <button
                className="purchase-plan"
                onClick={() => {
                  window.open("/checkout", "_blank");
                }}
                title="Purchase more time"
              >
                <PaymentIcon style={{ marginRight: "5px", fontSize: "16px" }} />
                Purchase Plan
              </button>
            </div>
            <div className="col-6">
              <div className="card interview-type cursor-pointer" onClick={() => {
                setInterView("webApp");
                setInterViewTime(0);
                setSessionTimeRemaining(sessionWebTimeRemaining);
              }}>
                <div className={interView === 'webApp' ? 'card-body type-active' : 'card-body'}>
                  <h3 className={interView === 'webApp' ? 'text-center type-active' : 'text-center'}>Web App</h3>
                </div>
              </div>
            </div>
            <div className="col-6">
              <div className="card interview-type cursor-pointer" onClick={() => {
                setInterView("desktopApp");
                setInterViewTime(sessionDesktopTimeRemaining < 60 ? sessionDesktopTimeRemaining : 60);
                setSessionTimeRemaining(sessionDesktopTimeRemaining);
              }}>
                <div className={interView === 'desktopApp' ? 'card-body type-active' : 'card-body'}>
                  <h3 className={interView === 'desktopApp' ? 'text-center type-active' : 'text-center'}>Desktop App</h3>
                </div>
              </div>
            </div>
          </div>
        </div>
        {interView === 'desktopApp' && (
          <div className="form-group">
            <label htmlFor="interViewTime">Interview Spend Time*</label>
            <input
              type="number"
              id="interViewTime"
              value={interViewTime}
              onChange={(e) => {
                const value = e.target.value;
                if (value > sessionDesktopTimeRemaining) {
                  setInterViewTime(sessionDesktopTimeRemaining);
                } else if (value < 0) {
                  setInterViewTime(0);
                } else {
                  setInterViewTime(value);
                }
              }}
              placeholder="e.g. 30 (in minutes)"
              required
            />
            <div className="row">
              {showDesktopError && <div className="col-12"><p className="error-text">{showDesktopError}</p></div>}
              <div className="col-12 text-end">
                <Link to="/installation" target="_blank" rel="noopener noreferrer" className="download-link">
                  Installation Instructions
                </Link>
              </div>
            </div>
          </div>
        )}
        <div className="form-group">
          <label htmlFor="jobTitle">Job Title*</label>
          <input
            type="text"
            id="jobTitle"
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
            placeholder="e.g. Frontend Developer"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="skillset">Your Skillset*</label>
          <input
            type="text"
            id="skillset"
            value={skillset}
            onChange={(e) => setSkillset(e.target.value)}
            placeholder="Input skills separated by commas (e.g. React, JavaScript, CSS)"
          />
        </div>

        <div className="form-group">
          <label htmlFor="jobDescription">Job Description</label>
          <textarea
            id="jobDescription"
            value={jobDescription}
            onChange={(e) => {
              setJobDescription(e.target.value);
              validateJobDescription(e.target.value);
            }}
            placeholder="Paste the job description here (max 1,000 words)"
            rows={6}
            className="job-description-textarea"
          />
          {jobDescriptionError && <p className="error-text">{jobDescriptionError}</p>}
        </div>

        <div className="form-group">
          <label htmlFor="resumeUpload">Resume (Optional)</label>
          <div className="file-upload-container">
            <div className="file-input-wrapper">
              <button type="button" className="file-input-button">
                Choose File
              </button>
              <input
                type="file"
                id="resumeUpload"
                onChange={handleFileChange}
                accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                className="file-input"
              />
              <span className="file-name">
                {resumeFile ? resumeFile.name : 'No file chosen'}
              </span>
            </div>

            {resumeFile && (
              <button
                type="button"
                className="clear-file"
                onClick={clearResume}
              >
                Remove
              </button>
            )}
          </div>
          {fileError && <p className="error-text">{fileError}</p>}
          <p className="help-text">Upload your resume to personalize interview questions (PDF or DOCX, max 5MB)</p>
          <p className="help-text"><strong>Note:</strong> Remove any sensitive information from your resume</p>
        </div>

        {/* <div className="form-group">
          <label>Record Your Voice (Optional - 2 mins max)</label>
          <div className="voice-recorder-container">
            {!audioUrl ? (
              <div className="recording-controls">
                <button
                  type="button"
                  className={`record-button ${isRecording ? 'recording' : ''}`}
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={isStartingSession}
                >
                  {isRecording ? (
                    <>
                      <StopIcon /> Stop Recording ({formatRecordingTime(recordingTime)})
                    </>
                  ) : (
                    <>
                      <MicIcon /> Start Recording
                    </>
                  )}
                </button>
                {isRecording && (
                  <p className="recording-indicator">Recording in progress... ({formatRecordingTime(recordingTime)})</p>
                )}
              </div>
            ) : (
              <div className="playback-controls">
                <div className="audio-info">
                  <span>Recording complete: {formatRecordingTime(recordingTime)}</span>
                </div>
                <div className="audio-buttons">
                  <button type="button" className="play-button" onClick={playRecording} disabled={isStartingSession}>
                    <PlayArrowIcon /> Play
                  </button>
                  <button type="button" className="delete-button" onClick={deleteRecording} disabled={isStartingSession}>
                    <DeleteIcon /> Delete
                  </button>
                </div>
              </div>
            )}
            <p className="help-text">Recording your voice helps the AI understand your speaking style and accent</p>
          </div>
        </div> */}

        <button 
          type="submit" 
          className="start-button" 
          disabled={isStartingSession}
        >
          {isStartingSession ? 'Starting Interview...' : 'Start Interview'}
        </button>
      </form>

      {/* Time Alert Dialog */}
      <Dialog
        open={showTimeAlert}
        onClose={() => setShowTimeAlert(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Session Time Information"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            You have {interView === 'desktopApp' ? interViewTime :  sessionTimeRemaining} minutes remaining in your session. Unlimited FREE minutes. Email info@interviewsupport-ai.com for more FREE minutes.
            Would you like to continue with the interview?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTimeAlert(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleTimeAlertConfirm} color="primary" autoFocus disabled={isStartingSession}>
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog
        open={showPaymentDialog}
        onClose={handlePaymentDialogClose}
        aria-labelledby="payment-dialog-title"
        aria-describedby="payment-dialog-description"
      >
        <DialogTitle id="payment-dialog-title">
          {"No Time Remaining"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="payment-dialog-description">
            You have no interview time remaining. Unlimited FREE minutes. Email info@interviewsupport-ai.com for more FREE minutes. Would you like to purchase more time to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePaymentDialogClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handlePaymentConfirm} color="primary" autoFocus>
            Go to Checkout
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default InterviewForm;
